import React, { useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowDownToLine,
  ArrowUpFromLine,
  Database,
  HardDrive,
  Loader2,
  Search,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@cms/ui/components/card";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { Alert, AlertTitle, AlertDescription } from "@cms/ui/components/alert";
import { formatBytes, formatCount } from "@cms/portal-lib/format";
import { fetchTenantUsage, type TenantUsageMetrics } from "../lib/usage";
import type { AdminOutletContext } from "../lib/outlet-context";

function StatCard({ title, icon: Icon, value, note }: {
  title: string;
  icon: LucideIcon;
  value: string;
  note?: string;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {note && <p className="mt-1 text-xs text-muted-foreground">{note}</p>}
      </CardContent>
    </Card>
  );
}

/** yyyy-mm-dd for a date input. */
function toInputDate(date: Date): string {
  return date.toISOString().split("T")[0];
}

/**
 * Cloudflare resource usage for one client over a date range (defaults to the
 * current calendar month): D1 storage and query activity, R2 storage and
 * operation counts, read from the analytics API via GET /tenant/usage.
 */
export function ClientUsagePage() {
  const { clientId } = useParams();
  const { api } = useOutletContext<AdminOutletContext>();

  const now = new Date();
  const [startDate, setStartDate] = useState(
    toInputDate(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)))
  );
  const [endDate, setEndDate] = useState(
    toInputDate(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)))
  );

  const [usage, setUsage] = useState<TenantUsageMetrics | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!api || !clientId || !startDate || !endDate) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    fetchTenantUsage(api, clientId, new Date(startDate), new Date(endDate))
      .then((metrics) => {
        if (active) setUsage(metrics);
      })
      .catch((err: Error) => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, clientId, startDate, endDate]);

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Database and file-storage usage for this client, from Cloudflare analytics.
        </p>
        <div className="flex items-end gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="usage-start">From</Label>
            <Input
              id="usage-start"
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
              className="w-40"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="usage-end">To</Label>
            <Input
              id="usage-end"
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
              className="w-40"
            />
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && !error && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && !error && usage && (
        <div className="space-y-6">
          <div>
            <h3 className="mb-3 text-base font-semibold">Database (D1)</h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard title="Storage used" icon={Database} value={formatBytes(usage.d1StorageBytes)} />
              <StatCard title="Queries" icon={Search} value={formatCount(usage.d1Queries)} />
              <StatCard title="Rows read" icon={ArrowDownToLine} value={formatCount(usage.d1RowsRead)} />
              <StatCard title="Rows written" icon={ArrowUpFromLine} value={formatCount(usage.d1RowsWritten)} />
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-base font-semibold">File storage (R2)</h3>
            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard title="Storage used" icon={HardDrive} value={formatBytes(usage.r2StorageBytes)} />
              <StatCard
                title="Class A operations"
                icon={ArrowUpFromLine}
                value={formatCount(usage.r2ClassAOperations)}
                note="Uploads and other writes"
              />
              <StatCard
                title="Class B operations"
                icon={ArrowDownToLine}
                value={formatCount(usage.r2ClassBOperations)}
                note="Downloads and reads"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
